import axios from 'axios'

const API_BASE = () => import.meta.env.VITE_BASE_LINK

/** Preferred display order for configured providers on login/register. */
const PROVIDER_ORDER = ['google', 'classlink']

/** Post-SSO landing for instructors who must pick an organization. Avoids /login. */
export const SSO_INSTRUCTOR_ORG_SELECT_PATH = '/choose-organization'

/** Providers the Learn UI supports. */
const SUPPORTED_PROVIDERS = new Set(['google', 'classlink'])

/** @type {{
 *   success: boolean,
 *   error: boolean,
 *   provider: string | null,
 *   ssoNewUser: boolean,
 *   ssoLinked: boolean,
 *   errorCode: string | null,
 *   errorMessage: string | null,
 * } | null} */
let pendingSsoResult = null

const SSO_ERROR_MESSAGES = {
  SSO_NOT_CONFIGURED: 'That sign-in method is not available right now. Please try again later.',
  SSO_INVALID_STATE: 'Your sign-in session expired. Please try again.',
  SSO_STATE_EXPIRED: 'Your sign-in session expired. Please try again.',
  SSO_EMAIL_NOT_VERIFIED: 'Your email is not verified with that provider. Verify it, then try again.',
  SSO_EMAIL_CONFLICT: 'This email is already linked to another account. Sign in with your password or contact support.',
  SSO_CALLBACK_FAILED: 'Sign-in failed. Please try again.',
  access_denied: 'Sign-in was cancelled.',
}

/**
 * Normalize a return path for the backend SSO start URL.
 * Must be a relative path starting with `/` (not `//`).
 */
export function getSafeReturnTo (path) {
  if (typeof path !== 'string') return '/dashboard'
  const trimmed = path.trim()
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return '/dashboard'
  }
  return trimmed
}

export function getSsoProviderLabel (providerId) {
  const id = String(providerId || '').toLowerCase()
  if (id === 'google') return 'Google'
  if (id === 'classlink') return 'ClassLink'
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : 'SSO'
}

/**
 * GET /auth/sso/providers — returns configured providers (id lowercased for UI).
 * @returns {Promise<Array<{ id: string, configured: boolean }>>}
 */
export async function fetchSsoProviders () {
  try {
    const { data } = await axios.get(`${API_BASE()}/auth/sso/providers`, {
      withCredentials: true,
    })
    const list = Array.isArray(data?.providers) ? data.providers : []
    return list.map((p) => ({
      id: String(p.id || '').toLowerCase(),
      configured: !!p.configured,
    }))
  } catch (error) {
    console.error('Failed to fetch SSO providers:', error)
    return []
  }
}

/**
 * Whether a specific SSO provider should be shown (configured === true).
 */
export function isSsoProviderConfigured (providers, providerId) {
  const id = String(providerId || '').toLowerCase()
  return (providers || []).some(
    (p) => p.id === id && p.configured === true
  )
}

/**
 * Whether Google SSO should be shown (configured === true).
 * @deprecated Prefer isSsoProviderConfigured(providers, 'google')
 */
export function isGoogleSsoConfigured (providers) {
  return isSsoProviderConfigured(providers, 'google')
}

/**
 * Configured providers only, sorted for UI (google, classlink).
 * @returns {Array<{ id: string, configured: boolean, label: string }>}
 */
export function getConfiguredSsoProviders (providers) {
  const configured = (providers || []).filter(
    (p) => p.configured && SUPPORTED_PROVIDERS.has(p.id)
  )
  return configured
    .slice()
    .sort((a, b) => {
      const ai = PROVIDER_ORDER.indexOf(a.id)
      const bi = PROVIDER_ORDER.indexOf(b.id)
      const aRank = ai === -1 ? PROVIDER_ORDER.length : ai
      const bRank = bi === -1 ? PROVIDER_ORDER.length : bi
      if (aRank !== bRank) return aRank - bRank
      return a.id.localeCompare(b.id)
    })
    .map((p) => ({
      ...p,
      label: getSsoProviderLabel(p.id),
    }))
}

/**
 * Start OIDC redirect flow via full browser navigation (not fetch/axios).
 * @param {string} providerId - e.g. 'google' | 'classlink'
 * @param {{ returnTo?: string }} [options]
 */
export function startSsoLogin (providerId, options = {}) {
  const provider = String(providerId || 'google').toLowerCase()
  const returnTo = getSafeReturnTo(options.returnTo)
  const url = new URL(`${API_BASE()}/auth/sso/${provider}`)
  if (returnTo) {
    url.searchParams.set('returnTo', returnTo)
  }
  window.location.assign(url.toString())
}

/**
 * Friendly message for SSO error codes.
 */
export function getSsoErrorMessage (code, fallbackMessage, providerId) {
  if (fallbackMessage && String(fallbackMessage).trim()) {
    return String(fallbackMessage)
  }
  const key = String(code || '')
  const label = getSsoProviderLabel(providerId || 'SSO')
  if (key === 'SSO_NOT_CONFIGURED') {
    return `${label} sign-in is not available right now. Please try again later.`
  }
  if (key === 'SSO_EMAIL_NOT_VERIFIED') {
    return `Your email is not verified with ${label}. Verify it, then try again.`
  }
  if (key === 'SSO_CALLBACK_FAILED') {
    return `${label} sign-in failed. Please try again.`
  }
  if (key === 'access_denied') {
    return `${label} sign-in was cancelled.`
  }
  return SSO_ERROR_MESSAGES[key] || `${label} sign-in failed. Please try again.`
}

/**
 * Toast copy after a successful SSO redirect.
 */
export function getSsoSuccessToastMessage ({ ssoNewUser, ssoLinked, provider } = {}) {
  const label = getSsoProviderLabel(provider || 'SSO')
  if (ssoNewUser) return `Account created with ${label}`
  if (ssoLinked) return `${label} connected to your existing account`
  return `Signed in with ${label}`
}

/**
 * Read SSO query params, clear them from the URL (replaceState), and cache
 * the result for login UI / boot toast handling.
 *
 * @returns {{
 *   success: boolean,
 *   error: boolean,
 *   provider: string | null,
 *   ssoNewUser: boolean,
 *   ssoLinked: boolean,
 *   errorCode: string | null,
 *   errorMessage: string | null,
 * } | null}
 */
export function consumeSsoRedirectParams () {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const sso = params.get('sso')
  const ssoError = params.get('ssoError')
  const ssoErrorMessage = params.get('ssoErrorMessage')
  const provider = (params.get('provider') || '').toLowerCase() || null

  if (sso !== 'success' && !ssoError) {
    return pendingSsoResult
  }

  const result = {
    success: sso === 'success',
    error: !!ssoError,
    provider,
    ssoNewUser: params.get('ssoNewUser') === '1',
    ssoLinked: params.get('ssoLinked') === '1',
    errorCode: ssoError || null,
    errorMessage: ssoError
      ? getSsoErrorMessage(ssoError, ssoErrorMessage, provider)
      : null,
  }

  clearSsoParamsFromUrl()
  pendingSsoResult = result
  return result
}

/**
 * Remove SSO-related search params without a navigation.
 */
export function clearSsoParamsFromUrl () {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const keys = [
    'sso',
    'provider',
    'ssoNewUser',
    'ssoLinked',
    'ssoError',
    'ssoErrorMessage',
  ]
  let changed = false
  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key)
      changed = true
    }
  })
  if (changed) {
    const next = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(window.history.state, '', next)
  }
}

export function getPendingSsoResult () {
  return pendingSsoResult
}

export function clearPendingSsoResult () {
  pendingSsoResult = null
}

/**
 * Instructors who belong to more than one organization must pick one at login
 * (email/password, MFA, and SSO).
 * @param {{ isUserInstructor?: boolean, organizations?: unknown[] } | null | undefined} session
 */
export function instructorNeedsOrganizationSelection (session) {
  return Boolean(
    session?.isUserInstructor &&
    Array.isArray(session.organizations) &&
    session.organizations.length > 1
  )
}

/**
 * Fresh SSO sign-in should show the same org picker email login does.
 * Session restore / refresh still jumps to the last organization.
 * @param {{
 *   ssoResult?: { success?: boolean } | null,
 *   session?: { isUserInstructor?: boolean, organizations?: unknown[], user?: { needsPasswordReset?: boolean } } | null,
 * }} [options]
 */
export function shouldPromptInstructorOrgSelectionAfterSso ({ ssoResult, session } = {}) {
  if (!ssoResult?.success) return false
  if (session?.user?.needsPasswordReset) return false
  return instructorNeedsOrganizationSelection(session)
}

/**
 * Restore session the same way boot / post-login does.
 * @returns {Promise<object>} authenticateUser response data
 */
export async function restoreSessionFromCookie () {
  const body = {
    apiKey: import.meta.env.VITE_APIKEY,
  }
  const { data } = await axios.post(
    `${API_BASE()}/authenticateUser`,
    body,
    { withCredentials: true }
  )
  return data
}

/**
 * GET /auth/sso/connections — current user's linked SSO providers + password flag.
 * @returns {Promise<{ hasPassword: boolean, connections: Array<{ id: string, linked: boolean, email: string|null, linkedAt: string|null }> }>}
 */
export async function fetchSsoConnections () {
  const { data } = await axios.get(`${API_BASE()}/auth/sso/connections`, {
    withCredentials: true,
  })
  const list = Array.isArray(data?.connections) ? data.connections : []
  return {
    hasPassword: !!data?.hasPassword,
    connections: list.map((c) => ({
      id: String(c.provider || c.id || '').toLowerCase(),
      linked: c.linked !== false,
      email: c.email || c.providerEmail || null,
      linkedAt: c.linkedAt || c.linked_at || null,
    })).filter((c) => c.id),
  }
}

/**
 * POST /auth/sso/unlink — disconnect a provider from the logged-in account.
 * @param {string} providerId
 */
export async function unlinkSsoProvider (providerId) {
  const provider = String(providerId || '').toUpperCase()
  const { data } = await axios.post(
    `${API_BASE()}/auth/sso/unlink`,
    { provider },
    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
  )
  return data
}

/**
 * POST /auth/sso/set-password — set a first password for SSO-only accounts (no current password).
 * @param {{ password: string, confirmPassword: string }} payload
 */
export async function setPasswordForSsoAccount ({ password, confirmPassword }) {
  const { data } = await axios.post(
    `${API_BASE()}/auth/sso/set-password`,
    { password, confirmPassword },
    { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
  )
  return data
}
